import { FC, useEffect, useState } from 'react';
import { NudosButton, NudosTextInput } from '../../../../../../components/NudosComponents';
import { IactiveLsStatusDto, IlogisticServiceDetails } from '../../../../../../types/services';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { editActiveServiceInformation } from '../../../../../../services/services';
import BordOneToneIcon from '../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import InfoDisplayItem from '../LogisticServiceDatesAndProgrammingDetails/InfoDisplayItem';
import { formatCost } from '../../../../../../utils/formatNumbers';
import './LogisticServiceProviderDetails.scss';

interface ILogisticServiceProviderDetails {
  logisticServiceData: IlogisticServiceDetails;
  updateWithoutLoader: () => void;
  showProviderInfo: boolean;
}

const LogisticServiceProviderDetails: FC<ILogisticServiceProviderDetails> = ({
  logisticServiceData,
  updateWithoutLoader,
  showProviderInfo
}: ILogisticServiceProviderDetails) => {
  const disableEditing = ['entregado'].includes(logisticServiceData.statusName || '');
  const [provierInfo, setProviderInfo] = useState<IactiveLsStatusDto>({
    deliveryProviderName: logisticServiceData?.provider || '',
    providerEmail: logisticServiceData?.providerEmail || '',
    totalOperatingExpenses: logisticServiceData?.totalOperatingExpenses || ''
  });
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>();
  const renderTotalOperatingExpenses =
    provierInfo?.totalOperatingExpenses === '' ? 0 : provierInfo?.totalOperatingExpenses;

  const renderProviderNameField = disableEditing ? (
    <InfoDisplayItem
      oneToneIconProps={{ variant: 'truck', standardSize: 16 }}
      text="Nombre del proveedor"
      value={provierInfo?.deliveryProviderName || ''}
      dateCustomClassName="text-nudos-text-color"
    />
  ) : (
    <NudosTextInput
      handleChange={e => {
        setHasChanges(true);
        setProviderInfo({ ...provierInfo, deliveryProviderName: e });
      }}
      defaultValue={String(provierInfo?.deliveryProviderName || '')}
      label="Nombre del proveedor"
      isFilled={!!provierInfo?.deliveryProviderName}
      placeholder="Ingresa el nombre"
    />
  );

  const renderProviderEmailField = disableEditing ? (
    <InfoDisplayItem
      oneToneIconProps={{ variant: 'mail', standardSize: 16 }}
      text="Correo del proveedor"
      value={provierInfo?.providerEmail || ''}
      dateCustomClassName="text-nudos-text-color"
    />
  ) : (
    <NudosTextInput
      handleChange={e => {
        setHasChanges(true);
        setProviderInfo({ ...provierInfo, providerEmail: e });
      }}
      defaultValue={String(provierInfo?.providerEmail || '')}
      label="Correo del proveedor"
      isFilled={!!provierInfo?.providerEmail}
      placeholder="Ingresa el correo"
    />
  );

  const renderPriceField = disableEditing ? (
    <InfoDisplayItem
      oneToneIconProps={{
        variant: 'dollarSign',
        standardSize: 16
      }}
      text="Precio del servicio"
      value={`$${formatCost(Number(renderTotalOperatingExpenses))} USD`}
      dateCustomClassName="text-nudos-text-color"
    />
  ) : (
    <NudosTextInput
      handleChange={e => {
        setHasChanges(true);
        setProviderInfo({ ...provierInfo, totalOperatingExpenses: e });
      }}
      defaultValue={String(provierInfo?.totalOperatingExpenses || '')}
      label="Precio del servicio"
      isFilled={!!provierInfo?.totalOperatingExpenses}
      placeholder="Ingresa el monto"
    />
  );

  const initialData = () => {
    if (logisticServiceData) {
      setProviderInfo({
        deliveryProviderName: logisticServiceData?.provider || '',
        providerEmail: logisticServiceData?.providerEmail || '',
        totalOperatingExpenses: logisticServiceData?.totalOperatingExpenses || ''
      });
    }
  };

  const saveProviderDetails = async () => {
    const providerDetails = provierInfo;
    if (providerDetails && logisticServiceData?.id) {
      setLoading(true);
      try {
        const updateBody = {
          ...providerDetails,
          totalOperatingExpenses: Number(providerDetails?.totalOperatingExpenses || 0)
        };
        await editActiveServiceInformation(logisticServiceData.id, updateBody);
        await updateWithoutLoader();
        displaySuccessNotification({ customJSXMessage: <>Información del proveedor actualizada correctamente</> });
        setHasChanges(false);
      } catch (error) {
        displayErrorNotification();
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    initialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticServiceData]);

  if (!showProviderInfo)
    return (
      <div className="emptyProviderDetails">
        <div className="dividerLine" />
        <div className="title">Información del proveedor</div>
        <div className="emptyProviderDetailsContainer">
          <div className="descriptionContainer">
            <div className="iconContainer">
              <BordOneToneIcon variant="truck" standardSize={52} />
            </div>
            <div className="description">En este espacio podrás consultar la información del proveedor</div>
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <div className="dividerLine" />
      <div id="logisticServiceProviderDetails">
        <div className="boxProviderDetails">
          <div className="titleLSProvider">Información del proveedor</div>
          <div className="minFormContainer">
            <div className="providerNameContainer">{renderProviderNameField}</div>
            {(provierInfo?.providerEmail || !disableEditing) && (
              <div className="providerEmailContainer">{renderProviderEmailField}</div>
            )}
            <div className="servicePriceContainer">{renderPriceField}</div>
            {!disableEditing && (
              <div className="buttonPriceContainer">
                <NudosButton
                  customClassName="buttonStyles"
                  customHeight="28px"
                  customWidth="69px"
                  buttonText="Guardar"
                  handleClick={() => {
                    saveProviderDetails();
                  }}
                  isButtonLoading={loading}
                  isButtonDisabled={!hasChanges || loading}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticServiceProviderDetails;
